import { useState, useEffect, useCallback } from 'react';
import { Mail, Zap, Eye, Send, RefreshCw, X, CheckCircle, Clock, AlertCircle, ChevronDown, ChevronUp, BarChart2, Edit2, Link, Shield } from 'lucide-react';
import AdminEmailAutomationSettings from './AdminEmailAutomationSettings';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_PILL = {
  draft:      { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200', label: 'Draft' },
  sending:    { bg: 'bg-blue-50',   text: 'text-blue-700',   border: 'border-blue-200',   label: 'Sending' },
  sent:       { bg: 'bg-green-50',  text: 'text-green-700',  border: 'border-green-200',  label: 'Sent' },
  cancelled:  { bg: 'bg-red-50',    text: 'text-red-700',    border: 'border-red-200',    label: 'Cancelled' },
  skipped:    { bg: 'bg-gray-50',   text: 'text-gray-600',   border: 'border-gray-200',   label: 'Skipped' },
  failed:     { bg: 'bg-red-50',    text: 'text-red-700',    border: 'border-red-200',    label: 'Failed' },
};

function StatusPill({ status }) {
  const s = STATUS_PILL[status] || STATUS_PILL.draft;
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${s.bg} ${s.text} ${s.border}`}>
      {s.label}
    </span>
  );
}

function StrategySection({ title, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-[#8A9A8B]/15 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 bg-white hover:bg-[#F6F1E9] transition-colors text-left"
      >
        <span className="text-sm font-bold text-[#2B2B2B]">{title}</span>
        {open ? <ChevronUp size={15} className="text-[#8A9A8B]" /> : <ChevronDown size={15} className="text-[#8A9A8B]" />}
      </button>
      {open && <div className="px-4 py-3 bg-[#F6F1E9]/50 border-t border-[#8A9A8B]/10">{children}</div>}
    </div>
  );
}

function PreviewModal({ campaignId, onClose }) {
  const [html, setHtml] = useState('');
  const [sizeKb, setSizeKb] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/admin/email-campaigns/${campaignId}/preview`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { setHtml(d.html || ''); setSizeKb(d.size_kb || 0); setLoading(false); })
      .catch(() => setLoading(false));
  }, [campaignId]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 bg-black/60 overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-2xl my-4 overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#8A9A8B]/15">
          <div>
            <h3 className="font-bold text-[#2B2B2B]">Email Preview</h3>
            {sizeKb > 0 && (
              <p className={`text-xs mt-0.5 ${sizeKb > 80 ? 'text-red-600 font-bold' : 'text-[#8A9A8B]'}`}>
                HTML size: {sizeKb} KB {sizeKb > 80 ? '⚠ Exceeds 80KB — Gmail may clip' : '✓ Gmail-safe'}
              </p>
            )}
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#F6F1E9] rounded-full">
            <X size={18} />
          </button>
        </div>
        <div className="p-4 max-h-[75vh] overflow-y-auto bg-[#f0ece4]">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-2 border-[#C8A97E] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : html ? (
            <iframe
              srcDoc={html}
              className="w-full border-0 rounded-xl"
              style={{ height: '600px' }}
              title="Email preview"
            />
          ) : (
            <p className="text-center text-[#8A9A8B] py-8">No preview available</p>
          )}
        </div>
      </div>
    </div>
  );
}

function TestEmailModal({ campaignId, onClose, onSent }) {
  const [emails, setEmails] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState('');

  const handleSend = async () => {
    const emailList = emails.split(',').map(e => e.trim()).filter(Boolean);
    if (!emailList.length) { setResult('Enter at least one email address'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/email-campaigns/${campaignId}/send-test`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ test_emails: emailList }),
      });
      const d = await res.json();
      setResult(d.success ? `Sent to ${d.sent} address(es)` : `Failed: ${d.error || 'Unknown error'}`);
      if (d.success) onSent?.();
    } catch (e) { setResult('Error: ' + e.message); }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-[#2B2B2B]">Send Test Email</h3>
          <button onClick={onClose} className="p-2 hover:bg-[#F6F1E9] rounded-full"><X size={16} /></button>
        </div>
        <p className="text-sm text-[#2B2B2B]/60 mb-3">Separate multiple addresses with commas.</p>
        <input
          className="w-full border border-[#8A9A8B]/30 rounded-xl px-4 py-3 text-sm mb-4 focus:outline-none focus:border-[#C8A97E]"
          placeholder="you@example.com, team@example.com"
          value={emails}
          onChange={e => setEmails(e.target.value)}
        />
        {result && <p className={`text-sm mb-3 font-semibold ${result.includes('Sent') ? 'text-green-600' : 'text-red-600'}`}>{result}</p>}
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 rounded-full border border-[#8A9A8B]/30 text-sm font-semibold text-[#2B2B2B]">Cancel</button>
          <button onClick={handleSend} disabled={loading} className="flex-1 px-4 py-2.5 rounded-full bg-[#2B2B2B] text-white text-sm font-bold disabled:opacity-50">
            {loading ? 'Sending...' : 'Send Test'}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditCampaignModal({ campaign, onClose, onSaved, showMsg }) {
  const [fields, setFields] = useState({
    subject:       campaign.subject || '',
    preheader:     campaign.plan?.preheader || '',
    headline:      campaign.plan?.headline || '',
    hook:          campaign.plan?.hook || '',
    primary_cta:   campaign.plan?.primary_cta || '',
    secondary_cta: campaign.plan?.secondary_cta || '',
  });
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/email-campaigns/${campaign.campaign_id}/edit`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(fields),
      });
      const d = await res.json();
      if (d.success) { showMsg('Campaign updated'); onSaved(); onClose(); }
      else showMsg('Error: ' + (d.detail || 'Update failed'));
    } catch (e) { showMsg('Error: ' + e.message); }
    setLoading(false);
  };

  const Field = ({ label, name, multiline }) => (
    <div className="mb-3">
      <label className="block text-xs font-bold text-[#2B2B2B]/60 uppercase tracking-wide mb-1">{label}</label>
      {multiline ? (
        <textarea rows={3} className="w-full border border-[#8A9A8B]/30 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#C8A97E] resize-none"
          value={fields[name]} onChange={e => setFields(p => ({...p, [name]: e.target.value}))} />
      ) : (
        <input className="w-full border border-[#8A9A8B]/30 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#C8A97E]"
          value={fields[name]} onChange={e => setFields(p => ({...p, [name]: e.target.value}))} />
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-lg my-4 p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-[#2B2B2B]">Edit Campaign</h3>
          <button onClick={onClose} className="p-2 hover:bg-[#F6F1E9] rounded-full"><X size={16} /></button>
        </div>
        <div className="max-h-[65vh] overflow-y-auto pr-1">
          <Field label="Subject Line" name="subject" />
          <Field label="Preheader" name="preheader" />
          <Field label="Headline" name="headline" />
          <Field label="Hook" name="hook" multiline />
          <Field label="Primary CTA" name="primary_cta" />
          <Field label="Secondary CTA" name="secondary_cta" />
        </div>
        <div className="flex gap-3 mt-4">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 rounded-full border border-[#8A9A8B]/30 text-sm font-semibold">Cancel</button>
          <button onClick={handleSave} disabled={loading} className="flex-1 px-4 py-2.5 rounded-full bg-[#2B2B2B] text-white text-sm font-bold disabled:opacity-50">
            {loading ? 'Saving...' : 'Save & Regenerate HTML'}
          </button>
        </div>
      </div>
    </div>
  );
}

function LinkValidation({ validation }) {
  if (!validation) return null;
  const { all_valid, results = [] } = validation;
  return (
    <div className={`rounded-xl p-3 ${all_valid ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
      <p className={`text-xs font-bold mb-2 flex items-center gap-1.5 ${all_valid ? 'text-green-700' : 'text-red-700'}`}>
        {all_valid ? <CheckCircle size={13} /> : <AlertCircle size={13} />}
        Deal Link Validation {all_valid ? '— All links passed' : '— Broken links found'}
      </p>
      <div className="space-y-1">
        {results.map((r, i) => (
          <div key={i} className="flex items-center gap-2 text-xs">
            {r.valid ? <CheckCircle size={11} className="text-green-500 flex-shrink-0" /> : <AlertCircle size={11} className="text-red-500 flex-shrink-0" />}
            <span className={r.valid ? 'text-green-700' : 'text-red-700 font-semibold'}>{r.business}: {r.title}</span>
            {!r.valid && <span className="text-red-500">— {r.error}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

function DailyContentPreview({ content }) {
  if (!content || !content.body) return null;
  return (
    <div className="bg-[#F6F1E9] rounded-xl p-3 border-l-3 border-[#C8A97E]" style={{ borderLeftWidth: '3px' }}>
      <p className="text-xs font-bold text-[#C8A97E] uppercase tracking-wide mb-1">Perkly Daily · {content.type?.replace(/_/g, ' ')}</p>
      <p className="text-sm font-semibold text-[#2B2B2B] mb-1">{content.title}</p>
      <p className="text-xs text-[#2B2B2B]/70 leading-relaxed">{content.body}</p>
    </div>
  );
}

function CampaignCard({ campaign, onRefresh, showMsg }) {
  const [expanded, setExpanded]       = useState(false);
  const [showPreview, setShowPreview]   = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [actionLoading, setActionLoading] = useState('');

  const handleApprove = async () => {
    if (!window.confirm('Send this campaign to all subscribers now?')) return;
    setActionLoading('approve');
    try {
      const res = await fetch(`${API}/admin/email-campaigns/${campaign.campaign_id}/approve`, {
        method: 'POST', credentials: 'include',
      });
      const d = await res.json();
      showMsg(d.success ? `Campaign sent to ${d.sent} recipients` : `Error: ${d.error || d.reason || d.detail || 'Send failed'}`);
      onRefresh();
    } catch (e) { showMsg('Error: ' + e.message); }
    setActionLoading('');
  };

  const handleCancel = async () => {
    setActionLoading('cancel');
    try {
      await fetch(`${API}/admin/email-campaigns/${campaign.campaign_id}/cancel`, {
        method: 'POST', credentials: 'include',
      });
      showMsg('Campaign cancelled');
      onRefresh();
    } catch (e) { showMsg('Error'); }
    setActionLoading('');
  };

  const plan = campaign.plan || {};
  const stats = campaign.stats || {};
  const isDraft  = campaign.status === 'draft';
  const isSent   = campaign.status === 'sent';
  const isSkipped = campaign.status === 'skipped';
  const sizeKb = campaign.html_size_kb || 0;

  return (
    <>
      {showPreview && <PreviewModal campaignId={campaign.campaign_id} onClose={() => setShowPreview(false)} />}
      {showTestModal && (
        <TestEmailModal campaignId={campaign.campaign_id}
          onClose={() => setShowTestModal(false)}
          onSent={() => showMsg('Test email sent!')} />
      )}
      {showEditModal && (
        <EditCampaignModal campaign={campaign}
          onClose={() => setShowEditModal(false)}
          onSaved={onRefresh} showMsg={showMsg} />
      )}

      <div className="bg-white rounded-2xl border border-[#8A9A8B]/15 overflow-hidden" data-testid={`campaign-card-${campaign.campaign_id}`}>
        {/* Header row */}
        <div className="px-5 py-4 flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <StatusPill status={campaign.status} />
              <span className="text-xs text-[#8A9A8B]">{new Date(campaign.created_at).toLocaleString()}</span>
              {sizeKb > 0 && (
                <span className={`text-xs font-semibold ${sizeKb > 80 ? 'text-red-500' : 'text-[#8A9A8B]'}`}>
                  {sizeKb} KB {sizeKb <= 80 ? '✓' : '⚠'}
                </span>
              )}
            </div>
            {campaign.subject ? (
              <p className="font-bold text-[#2B2B2B] text-sm leading-snug">{campaign.subject}</p>
            ) : (
              <p className="text-[#8A9A8B] text-sm italic">{campaign.skip_reason || 'No subject'}</p>
            )}
            {campaign.objective_label && (
              <p className="text-xs text-[#C8A97E] font-semibold mt-0.5">{campaign.objective_label}</p>
            )}
          </div>

          <div className="flex items-center gap-1.5 flex-shrink-0">
            {isDraft && !isSkipped && (
              <button onClick={() => setShowEditModal(true)} title="Edit"
                className="p-2 rounded-full hover:bg-[#F6F1E9] text-[#8A9A8B] hover:text-[#2B2B2B]">
                <Edit2 size={15} />
              </button>
            )}
            {!isSkipped && campaign.subject && (
              <button onClick={() => setShowPreview(true)} title="Preview"
                className="p-2 rounded-full hover:bg-[#F6F1E9] text-[#8A9A8B] hover:text-[#2B2B2B]">
                <Eye size={16} />
              </button>
            )}
            <button onClick={() => setExpanded(!expanded)} title="Details"
              className="p-2 rounded-full hover:bg-[#F6F1E9] text-[#8A9A8B] hover:text-[#2B2B2B]">
              {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
          </div>
        </div>

        {/* Stats row for sent campaigns */}
        {isSent && (
          <div className="px-5 pb-4 grid grid-cols-4 gap-3">
            {[
              { label: 'Sent', value: stats.delivered || stats.recipients || 0 },
              { label: 'Opens', value: stats.opens || 0 },
              { label: 'Clicks', value: stats.clicks || 0 },
              { label: 'Claims', value: stats.claim_starts || 0 },
            ].map(s => (
              <div key={s.label} className="bg-[#F6F1E9] rounded-xl p-3 text-center">
                <p className="text-lg font-bold text-[#2B2B2B]">{s.value}</p>
                <p className="text-xs text-[#8A9A8B]">{s.label}</p>
              </div>
            ))}
          </div>
        )}

        {/* Action buttons for draft campaigns */}
        {isDraft && !isSkipped && (
          <div className="px-5 pb-4 flex gap-2 flex-wrap">
            {campaign.link_validation && !campaign.link_validation.all_valid ? (
              <div className="w-full mb-2">
                <p className="text-xs text-red-600 font-bold flex items-center gap-1">
                  <AlertCircle size={12} /> Cannot send — broken deal links found. Edit or regenerate.
                </p>
              </div>
            ) : null}
            <button onClick={handleApprove}
              disabled={!!actionLoading || (campaign.link_validation && !campaign.link_validation.all_valid)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#2B2B2B] text-white text-xs font-bold disabled:opacity-50"
              data-testid="approve-send-btn">
              <Send size={13} /> {actionLoading === 'approve' ? 'Sending...' : 'Approve & Send'}
            </button>
            <button onClick={() => setShowEditModal(true)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#F6F1E9] text-[#2B2B2B] text-xs font-bold border border-[#8A9A8B]/20">
              <Edit2 size={13} /> Edit
            </button>
            <button onClick={() => setShowTestModal(true)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#C8A97E]/15 text-[#2B2B2B] text-xs font-bold border border-[#C8A97E]/40">
              <Mail size={13} /> Send Test
            </button>
            <button onClick={handleCancel} disabled={!!actionLoading}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full border border-red-200 text-red-600 text-xs font-bold">
              <X size={13} /> Cancel
            </button>
          </div>
        )}

        {/* Expanded strategy detail */}
        {expanded && !isSkipped && (
          <div className="border-t border-[#8A9A8B]/10 px-5 py-4 space-y-3">
            {/* Creative */}
            <StrategySection title="Creative Decisions">
              <div className="space-y-2 text-sm">
                {[
                  ['Subject', campaign.subject],
                  ['Preheader', plan.preheader],
                  ['Headline', plan.headline],
                  ['Hook', plan.hook],
                  ['Template', plan.template],
                  ['Primary CTA', plan.primary_cta],
                  ['Secondary CTA', plan.secondary_cta],
                ].filter(([,v]) => v).map(([k,v]) => (
                  <div key={k} className="flex gap-2">
                    <span className="text-[#8A9A8B] font-semibold w-24 flex-shrink-0 text-xs uppercase tracking-wide">{k}</span>
                    <span className="text-[#2B2B2B]">{v}</span>
                  </div>
                ))}
              </div>
            </StrategySection>

            {/* AI Reasoning */}
            <StrategySection title="AI Decision Explanation">
              <div className="space-y-3 text-sm">
                {[
                  ['Campaign objective', plan.campaign_rationale],
                  ['Subject rationale', plan.subject_rationale],
                  ['Hero deal choice', plan.hero_rationale],
                  ['Format choice', plan.format_rationale],
                  ['Learning applied', plan.learning_applied],
                  ['Repetition avoided', plan.repetition_avoided],
                ].filter(([,v]) => v).map(([k,v]) => (
                  <div key={k}>
                    <p className="text-[#8A9A8B] font-semibold text-xs uppercase tracking-wide mb-0.5">{k}</p>
                    <p className="text-[#2B2B2B] leading-relaxed">{v}</p>
                  </div>
                ))}
              </div>
            </StrategySection>

            {/* Technical */}
            <StrategySection title="Technical Validation">
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  {sizeKb <= 80
                    ? <CheckCircle size={14} className="text-green-500" />
                    : <AlertCircle size={14} className="text-red-500" />}
                  <span className={sizeKb > 80 ? 'text-red-600 font-semibold' : 'text-[#2B2B2B]'}>
                    HTML size: {sizeKb} KB {sizeKb > 80 ? '— Gmail clipping risk!' : '— Gmail safe'}
                  </span>
                </div>
                <div className="flex items-center gap-2"><CheckCircle size={14} className="text-green-500" /><span className="text-[#2B2B2B]">Table-based layout (email-client compatible)</span></div>
                <div className="flex items-center gap-2"><CheckCircle size={14} className="text-green-500" /><span className="text-[#2B2B2B]">Unsubscribe link included</span></div>
                <div className="flex items-center gap-2"><CheckCircle size={14} className="text-green-500" /><span className="text-[#2B2B2B]">Open tracking pixel included</span></div>
                <div className="flex items-center gap-2"><CheckCircle size={14} className="text-green-500" /><span className="text-[#2B2B2B]">UTM tracking on all deal links</span></div>
                <div className="flex items-center gap-2"><CheckCircle size={14} className="text-green-500" /><span className="text-[#2B2B2B]">Click tracking redirects to correct deal page</span></div>
              </div>
              {campaign.link_validation && (
                <div className="mt-3">
                  <LinkValidation validation={campaign.link_validation} />
                </div>
              )}
            </StrategySection>

            {/* Daily Content */}
            {campaign.daily_content && campaign.daily_content.body && (
              <StrategySection title="Perkly Daily Content Section">
                <DailyContentPreview content={campaign.daily_content} />
              </StrategySection>
            )}
          </div>
        )}
      </div>
    </>
  );
}

export default function AdminEmailCampaigns({ showMsg }) {
  const [campaigns, setCampaigns]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [generating, setGenerating] = useState(false);
  const [genMode, setGenMode]       = useState('review');
  const [subTab, setSubTab]         = useState('campaigns'); // 'campaigns' | 'automation'

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/email-campaigns`, { credentials: 'include' });
      const d = await res.json();
      setCampaigns(d.campaigns || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${API}/admin/email-campaigns/generate`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode: genMode }),
      });
      const d = await res.json();
      if (d.success) {
        showMsg(`Campaign generated (${d.html_size_kb || '?'} KB) — "${d.subject || 'No subject'}"`);
        fetchCampaigns();
      } else {
        showMsg(`Skipped: ${d.reason || 'Unknown reason'}`);
        fetchCampaigns();
      }
    } catch (e) { showMsg('Error: ' + e.message); }
    setGenerating(false);
  };

  return (
    <div data-testid="email-campaigns-tab">
      {/* Sub-tab switcher */}
      <div className="flex gap-1 mb-6 bg-[#F6F1E9] rounded-xl p-1 w-fit">
        <button
          onClick={() => setSubTab('campaigns')}
          data-testid="subtab-campaigns"
          className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${subTab === 'campaigns' ? 'bg-white text-[#2B2B2B] shadow-sm' : 'text-[#2B2B2B]/50 hover:text-[#2B2B2B]'}`}
        >
          <Mail size={13} /> Campaigns
        </button>
        <button
          onClick={() => setSubTab('automation')}
          data-testid="subtab-automation"
          className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${subTab === 'automation' ? 'bg-white text-[#2B2B2B] shadow-sm' : 'text-[#2B2B2B]/50 hover:text-[#2B2B2B]'}`}
        >
          <Shield size={13} /> Email Automation
        </button>
      </div>

      {/* Automation settings */}
      {subTab === 'automation' && (
        <AdminEmailAutomationSettings showMsg={showMsg} />
      )}

      {/* Campaigns list */}
      {subTab === 'campaigns' && (<>
      {/* Header */}
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-bold text-[#2B2B2B]" style={{ fontFamily: 'Playfair Display, serif' }}>
            AI Email Campaigns
          </h2>
          <p className="text-sm text-[#2B2B2B]/55 mt-1">
            Intelligent daily email marketing — AI plans every campaign, reviews real deals and past performance.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={genMode}
            onChange={e => setGenMode(e.target.value)}
            className="border border-[#8A9A8B]/30 rounded-xl px-3 py-2 text-sm text-[#2B2B2B] focus:outline-none focus:border-[#C8A97E]"
          >
            <option value="review">Review Mode (save as draft)</option>
            <option value="auto">Auto Mode (send immediately)</option>
            <option value="test">Test Mode</option>
          </select>
          <button
            onClick={handleGenerate}
            disabled={generating}
            className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#2B2B2B] text-white text-sm font-bold disabled:opacity-60"
            data-testid="generate-campaign-btn"
          >
            <Zap size={15} />
            {generating ? 'Generating...' : 'Generate Campaign'}
          </button>
          <button onClick={fetchCampaigns} className="p-2.5 rounded-full border border-[#8A9A8B]/20 hover:bg-[#F6F1E9]">
            <RefreshCw size={16} className="text-[#8A9A8B]" />
          </button>
        </div>
      </div>

      {/* Mode description */}
      <div className="bg-[#F6F1E9] border border-[#C8A97E]/20 rounded-xl px-4 py-3 mb-6 text-sm text-[#2B2B2B]/70">
        {genMode === 'review' && <><strong className="text-[#2B2B2B]">Review Mode:</strong> AI generates a campaign and saves it as a draft. You review and approve before it sends.</>}
        {genMode === 'auto' && <><strong className="text-[#2B2B2B]">Auto Mode:</strong> AI generates and immediately sends the campaign to all subscribers.</>}
        {genMode === 'test' && <><strong className="text-[#2B2B2B]">Test Mode:</strong> Generates a draft, then you can send it to test email addresses only.</>}
      </div>

      {/* Campaign list */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-2 border-[#C8A97E] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : campaigns.length === 0 ? (
        <div className="text-center py-16 text-[#8A9A8B]">
          <Mail size={40} className="mx-auto mb-3 opacity-40" />
          <p className="font-semibold">No campaigns yet</p>
          <p className="text-sm mt-1">Click "Generate Campaign" to create your first AI-powered email.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {campaigns.map(c => (
            <CampaignCard
              key={c.campaign_id}
              campaign={c}
              onRefresh={fetchCampaigns}
              showMsg={showMsg}
            />
          ))}
        </div>
      )}
      </>)}
    </div>
  );
}
